import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUser, getUserPermissions, updateUserPermissions, getPermissionTemplates, applyPermissionTemplate } from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import { ArrowLeft, Save, Shield, CheckCircle } from 'lucide-react';

const ENTITIES = [
  { key: 'accounts', label: 'Accounts' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'opportunities', label: 'Opportunities' },
  { key: 'quotes', label: 'Quotes' },
  { key: 'orders', label: 'Orders' },
  { key: 'activities', label: 'Activities' },
  { key: 'tasks', label: 'Action Items' },
  { key: 'products', label: 'Products' },
  { key: 'rep_firms', label: 'Rep Firms' },

];

// Entities that have no ownership concept — only "all" or "none"
const NO_OWN_ENTITIES = ['products', 'rep_firms'];

const DEFAULT_PERMISSIONS = ENTITIES.map(e => ({
  entity: e.key,
  view_level: 'none',
  edit_level: 'none',
  create_allowed: false,
  delete_level: 'none',
  ...(e.key === 'opportunities' ? { price_override: 0, max_discount_pct: 0 } : {}),
}));

function getSelectColor(value) {
  if (value === 'all' || value === true || value === 'Allowed') return '#16a34a';
  if (value === 'own') return '#d97706';
  return '#dc2626';
}

function UserPermissions() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [userData, setUserData] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [templateConfirm, setTemplateConfirm] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [userRes, permsRes, templatesRes] = await Promise.all([
        getUser(id),
        getUserPermissions(id),
        getPermissionTemplates(),
      ]);
      setUserData(userRes.data);
      setTemplates(templatesRes.data || []);

      // Merge returned permissions with defaults so every entity has a row
      const permsData = permsRes.data?.permissions || permsRes.data || [];
      const merged = ENTITIES.map(e => {
        const found = Array.isArray(permsData) ? permsData.find(p => p.entity === e.key) : null;
        if (found) {
          // For no-own entities, normalize "own" to "none"
          const isNoOwn = NO_OWN_ENTITIES.includes(e.key);
          const base = {
            entity: e.key,
            view_level: isNoOwn && found.view_level === 'own' ? 'none' : found.view_level || 'none',
            edit_level: isNoOwn && found.edit_level === 'own' ? 'none' : found.edit_level || 'none',
            create_allowed: !!found.create_allowed,
            delete_level: isNoOwn && found.delete_level === 'own' ? 'none' : found.delete_level || 'none',
          };
          if (e.key === 'opportunities') {
            base.price_override = found.price_override ? 1 : 0;
            base.max_discount_pct = Number(found.max_discount_pct) || 0;
          }
          return base;
        }
        const defaultRow = { entity: e.key, view_level: 'none', edit_level: 'none', create_allowed: false, delete_level: 'none' };
        if (e.key === 'opportunities') {
          defaultRow.price_override = 0;
          defaultRow.max_discount_pct = 0;
        }
        return defaultRow;
      });

      setPermissions(merged);
      setOriginalPermissions(JSON.parse(JSON.stringify(merged)));
      setHasChanges(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Track unsaved changes
  useEffect(() => {
    if (originalPermissions.length === 0) return;
    const changed = JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
    setHasChanges(changed);
  }, [permissions, originalPermissions]);

  const handlePermissionChange = (entity, field, value) => {
    setPermissions(prev => prev.map(p => {
      if (p.entity !== entity) return p;
      if (field === 'create_allowed') return { ...p, [field]: value === 'true' };
      if (field === 'price_override') return { ...p, [field]: value ? 1 : 0 };
      if (field === 'max_discount_pct') return { ...p, [field]: Math.min(100, Math.max(0, Number(value) || 0)) };
      return { ...p, [field]: value };
    }));
    setSuccess('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await updateUserPermissions(id, permissions);
      setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
      setHasChanges(false);
      setSuccess('Permissions saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTemplate = async () => {
    if (!templateConfirm) return;
    try {
      setError('');
      setSuccess('');
      await applyPermissionTemplate(id, templateConfirm.id);
      setTemplateConfirm(null);
      await loadData();
      setSuccess(`Template "${templateConfirm.name}" applied successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply template');
      setTemplateConfirm(null);
    }
  };

  if (!hasPermission('manage_users')) {
    return <div className="alert alert-error">You do not have permission to manage user permissions.</div>;
  }

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  const viewEditDeleteOptions = (entity) => {
    const isNoOwn = NO_OWN_ENTITIES.includes(entity);
    const options = [
      { value: 'all', label: 'All Records' },
    ];
    if (!isNoOwn) {
      options.push({ value: 'own', label: 'Own Only' });
    }
    options.push({ value: 'none', label: 'None' });
    return options;
  };

  const createOptions = [
    { value: 'true', label: 'Allowed' },
    { value: 'false', label: 'Not Allowed' },
  ];

  return (
    <div className="user-permissions-page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button className="btn btn-icon" onClick={() => navigate(-1)} title="Go Back">
            <ArrowLeft size={20} />
          </button>
          <h1 className="page-title" style={{ margin: 0 }}>User Permissions</h1>
          {hasChanges && (
            <span className="badge badge-warning ml-2">Unsaved Changes</span>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* User Info Card */}
      {userData && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Shield size={24} style={{ color: 'var(--primary-color, #3b82f6)' }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{userData.name}</div>
              <div className="text-secondary" style={{ fontSize: '0.875rem' }}>
                {userData.email} {userData.role_name && <span> &middot; {userData.role_name}</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Quick-Apply */}
      {templates.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">Quick Apply Template</h3>
          </div>
          <div className="card-body">
            <p className="text-secondary" style={{ marginBottom: '0.75rem', fontSize: '0.875rem' }}>
              Apply a permission template to quickly configure this user's access. This will overwrite current permissions.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {templates.map(t => (
                <button
                  key={t.id}
                  className="btn btn-secondary btn-sm"
                  onClick={() => setTemplateConfirm(t)}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Permissions Matrix */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Permissions Matrix</h3>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ minWidth: '160px' }}>Entity</th>
                <th style={{ minWidth: '140px' }}>View</th>
                <th style={{ minWidth: '140px' }}>Edit</th>
                <th style={{ minWidth: '140px' }}>Create</th>
                <th style={{ minWidth: '140px' }}>Delete</th>
                <th style={{ minWidth: '110px' }}>Price Override</th>
                <th style={{ minWidth: '120px' }}>Max Discount %</th>
              </tr>
            </thead>
            <tbody>
              {permissions.map(perm => {
                const entityConfig = ENTITIES.find(e => e.key === perm.entity);
                if (!entityConfig) return null;
                const options = viewEditDeleteOptions(perm.entity);

                return (
                  <tr key={perm.entity}>
                    <td style={{ fontWeight: 500 }}>{entityConfig.label}</td>
                    <td>
                      <select
                        className="form-select"
                        value={perm.view_level}
                        onChange={(e) => handlePermissionChange(perm.entity, 'view_level', e.target.value)}
                        style={{
                          color: getSelectColor(perm.view_level),
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          padding: '0.35rem 0.5rem',
                          minWidth: '120px',
                        }}
                      >
                        {options.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="form-select"
                        value={perm.edit_level}
                        onChange={(e) => handlePermissionChange(perm.entity, 'edit_level', e.target.value)}
                        style={{
                          color: getSelectColor(perm.edit_level),
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          padding: '0.35rem 0.5rem',
                          minWidth: '120px',
                        }}
                      >
                        {options.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="form-select"
                        value={String(perm.create_allowed)}
                        onChange={(e) => handlePermissionChange(perm.entity, 'create_allowed', e.target.value)}
                        style={{
                          color: getSelectColor(perm.create_allowed),
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          padding: '0.35rem 0.5rem',
                          minWidth: '120px',
                        }}
                      >
                        {createOptions.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="form-select"
                        value={perm.delete_level}
                        onChange={(e) => handlePermissionChange(perm.entity, 'delete_level', e.target.value)}
                        style={{
                          color: getSelectColor(perm.delete_level),
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          padding: '0.35rem 0.5rem',
                          minWidth: '120px',
                        }}
                      >
                        {options.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {perm.entity === 'opportunities' ? (
                        <input
                          type="checkbox"
                          checked={!!perm.price_override}
                          onChange={(e) => handlePermissionChange(perm.entity, 'price_override', e.target.checked)}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                      ) : (
                        <span className="text-secondary">--</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {perm.entity === 'opportunities' ? (
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={perm.max_discount_pct}
                          onChange={(e) => handlePermissionChange(perm.entity, 'max_discount_pct', e.target.value)}
                          className="form-input"
                          style={{
                            width: '68px',
                            fontSize: '0.85rem',
                            padding: '0.35rem 0.5rem',
                            textAlign: 'center',
                          }}
                        />
                      ) : (
                        <span className="text-secondary">--</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', padding: '1rem', borderTop: '1px solid var(--border-color)' }}>
          {hasChanges && <span className="text-secondary" style={{ fontSize: '0.85rem' }}>You have unsaved changes</span>}
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <>Saving...</>
            ) : (
              <><Save size={16} style={{ marginRight: '0.35rem' }} /> Save Permissions</>
            )}
          </button>
        </div>
      </div>

      {/* Template Apply Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!templateConfirm}
        onClose={() => setTemplateConfirm(null)}
        onConfirm={handleApplyTemplate}
        title="Apply Permission Template"
        message={templateConfirm ? `Apply the "${templateConfirm.name}" template to ${userData?.name || 'this user'}? This will overwrite all current permissions.` : ''}
        confirmText="Apply Template"
      />
    </div>
  );
}

export default UserPermissions;
